import axios from "axios";
import { baseUrl } from ".";

export interface FavoriteProperty {
  _id: string;
  propertyOf: string;
  price: number;
  images: Array<{ url: string }>;
  bedrooms: number;
  buildingUnits?: number;
  region: string;
  strategy: string;
  tenureType: string;
  occupancy: string;
  discountToMarket: number;
  rentGrossYield: number;
  estimatedValue: number;
  investment: number;
  averageRent: number;
  reserved: boolean;
}

export interface ToggleFavoriteResponse {
  message: string;
  favorites: string[];
  isFavorite: boolean;
  status: number;
}

export interface GetFavoritesResponse {
  message: string;
  results: FavoriteProperty[];
  favoriteIds: string[];
  status: number;
}

/**
 * Toggle a property as favorite (add or remove)
 * @param propertyId The ID of the property to toggle
 * @param token The auth token from the store
 * @returns Response with status and favorites list
 */
export const toggleFavoriteProperty = async (
  propertyId: string,
  token: string
): Promise<ToggleFavoriteResponse> => {
  try {
    if (!token) {
      return {
        message: "Authentication required",
        favorites: [],
        isFavorite: false,
        status: 401
      };
    }

    const response = await axios.post(
      `${baseUrl}/whitelabel/toggleFavoriteProperty`,
      { propertyId },
      {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token.replace("B9I9N9456", "")}`
        },
      }
    );

    return { 
      status: 200, 
      message: response.data.message,
      favorites: response.data.favorites || [],
      isFavorite: response.data.isFavorite
    };
  } catch (error: any) {
    console.error("Error toggling favorite property:", error);
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || "An error occurred while toggling favorite",
      favorites: [],
      isFavorite: false
    };
  }
};

/**
 * Get all favorite properties for the current user
 * @param token The auth token from the store
 * @returns Response with status and list of favorite properties
 */
export const getFavoriteProperties = async (
  token: string
): Promise<GetFavoritesResponse> => {
  try {
    if (!token) {
      return {
        message: "Authentication required",
        results: [],
        favoriteIds: [],
        status: 401
      };
    }

    const response = await axios.get(
      `${baseUrl}/whitelabel/favoriteProperties`,
      {
        headers: {
          "Authorization": `Bearer ${token.replace("B9I9N9456", "")}`
        },
      }
    );

    return { 
      status: 200, 
      message: response.data.message,
      results: response.data.results || [],
      favoriteIds: response.data.favoriteIds || []
    };
  } catch (error: any) {
    console.error("Error fetching favorite properties:", error);
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || "An error occurred while fetching favorites",
      results: [],
      favoriteIds: []
    };
  }
};

/**
 * Check if a property is in the user's favorites
 * @param propertyId The ID of the property to check
 * @param token The auth token from the store
 * @returns Response with status and whether the property is a favorite
 */
export const isPropertyFavorite = async (
  propertyId: string,
  token: string
): Promise<{ status: number; isFavorite: boolean; message: string }> => {
  try {
    if (!token) {
      return {
        message: "Authentication required",
        isFavorite: false,
        status: 401
      };
    }

    const response = await axios.get(
      `${baseUrl}/whitelabel/isFavorite/${propertyId}`,
      {
        headers: {
          "Authorization": `Bearer ${token.replace("B9I9N9456", "")}`
        },
      }
    );

    return { 
      status: 200, 
      message: response.data.message,
      isFavorite: response.data.isFavorite
    };
  } catch (error: any) {
    console.error("Error checking if property is favorite:", error);
    return {
      status: error.response?.status || 500,
      message: error.response?.data?.message || "An error occurred while checking favorite status",
      isFavorite: false
    };
  }
};